import {
  Badge,
  Button,
  buttonVariants,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  confirm,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import {
  RiAddLine,
  RiArrowDownLine,
  RiArrowLeftLine,
  RiArrowUpLine,
  RiCheckLine,
  RiDeleteBinLine,
  RiDragMove2Line,
  RiSaveLine,
} from "@remixicon/react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Link,
  useBlocker,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { CATALOG_PAGE_LIMIT } from "@/modules/cf-feed/presentation/api/queries";
import type { AdminCatalogItem } from "@/modules/cf-feed/presentation/api/types";
import { updateCatalogSelection } from "@/modules/cf-feed/presentation/lib/catalog-add";
import { useFacebookPagesQuery } from "@/modules/facebook/presentation/api/queries";
import { FacebookPageCombobox } from "@/modules/facebook/presentation/ui/FacebookPageCombobox";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { QueryState } from "@/shared/ui/QueryState";
import type { PriceInput } from "../../domain/contracts/live-workspace";
import { productSetApi } from "../api/product-set-client";
import {
  WorkspaceRequestError,
  workspaceErrorMessage,
} from "../api/workspace-client";
import {
  editProductSetPrices,
  type ProductSetPrices,
  productSetEstimate,
  productSetItems,
} from "../lib/product-set-draft";
import { ProductSetDestination } from "../ui/ProductSetDestination";
import { WorkspaceCatalogCards } from "../ui/WorkspaceCatalogCards";
import { WorkspaceCatalogPriceDialog } from "../ui/WorkspaceCatalogPriceDialog";
import {
  WorkspaceProductCard,
  type WorkspaceProductCardAction,
} from "../ui/WorkspaceProductCard";
import { kip, WorkspaceSelect } from "../ui/WorkspaceProductEditor";

const listKey = (pageId: string) => ["cf-product-sets", pageId] as const;
const emptyPrice: PriceInput = { mode: "inherit" };

function priceSummary(price: PriceInput) {
  if (price.mode === "inherit") return "ໃຊ້ໂປຣຂອງງານ";
  if (price.mode === "none") return "ບໍ່ຫຼຸດລາຄາ";
  if (price.mode === "percent") return `ຫຼຸດ ${price.value ?? 0}%`;
  if (price.mode === "fixed") return `ຫຼຸດ ${price.value ?? 0} ກີບ`;
  return `${price.value ?? 0} ກີບ`;
}

function ProductSetPrice({
  item,
  price,
}: {
  item: AdminCatalogItem;
  price: PriceInput;
}) {
  let after: number | null = null;
  let error: unknown;
  try {
    after = productSetEstimate(Number(item.price), price);
  } catch (caught) {
    error = caught;
  }
  return (
    <div className="space-y-1 whitespace-normal text-xs tabular-nums">
      <p className="text-muted-foreground">ປົກກະຕິ {kip(Number(item.price))}</p>
      <p>{priceSummary(price)}</p>
      <strong className="block">
        {error
          ? workspaceErrorMessage(error)
          : after === null
            ? "ລາຄາຂາຍ: ກວດໃນງານ"
            : `ຫຼັງຫຼຸດ ${kip(after)}`}
      </strong>
      <p className="text-muted-foreground">
        {price.mode === "inherit"
          ? "ແຫຼ່ງລາຄາ: ໂປຣຂອງ Feed / Live"
          : "ແຫຼ່ງລາຄາ: ກົດຂອງ SKU ໃນຊຸດ"}
      </p>
    </div>
  );
}

function PagePicker({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) {
  const pages = useFacebookPagesQuery();
  return (
    <div className="max-w-xl space-y-1">
      <p className="text-muted-foreground text-xs">ເພຈ Facebook</p>
      <FacebookPageCombobox
        pages={
          pages.data?.pages.filter((page) => page.status !== "disconnected") ??
          []
        }
        value={value ?? ""}
        onValueChange={onChange}
        placeholder="ເລືອກເພຈທີ່ຈະຈັດການຊຸດສິນຄ້າ"
        disabled={pages.isLoading}
        queryKey={["cf-product-set-pages"]}
      />
    </div>
  );
}

export function ProductSetsPage() {
  const { has } = usePermissions();
  const canManage = has("live:manage");
  const search = useSearch({ from: "/app/product-sets" });
  const nav = useNavigate({ from: "/app/product-sets" });
  const pageId = search.pageId;
  const sets = useQuery({
    queryKey: listKey(pageId ?? ""),
    queryFn: () => productSetApi.list(pageId ?? ""),
    enabled: Boolean(pageId),
  });
  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto max-w-5xl space-y-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="font-semibold text-2xl">ຊຸດສິນຄ້າ</h1>
              <p className="text-muted-foreground text-sm">
                ຕັ້ງ SKU ແລະລາຄາໄວ້ ແລ້ວນຳໄປໃຊ້ກັບ Feed ຫຼື Live
              </p>
            </div>
            <Button
              disabled={!pageId || !canManage}
              onClick={() =>
                nav({
                  to: "/app/product-sets/$id",
                  params: { id: "new" },
                  search: { pageId },
                })
              }
            >
              <RiAddLine size={18} /> ສ້າງຊຸດສິນຄ້າ
            </Button>
          </div>
          <PagePicker
            value={pageId}
            onChange={(next) => nav({ search: { pageId: next } })}
          />
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-56">ຊື່ຊຸດສິນຄ້າ</TableHead>
                  <TableHead className="text-right">SKU</TableHead>
                  <TableHead className="min-w-40">ລາຄາ</TableHead>
                  <TableHead className="hidden min-w-40 lg:table-cell">
                    ແກ້ໄຂລ່າສຸດ
                  </TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {!pageId ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      ເລືອກເພຈກ່ອນເພື່ອເບິ່ງ ຫຼື ສ້າງຊຸດສິນຄ້າ
                    </TableCell>
                  </TableRow>
                ) : sets.isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      ກຳລັງໂຫຼດຊຸດສິນຄ້າ…
                    </TableCell>
                  </TableRow>
                ) : sets.isError ? (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      <Button
                        variant="outline"
                        onClick={() => void sets.refetch()}
                      >
                        ລອງໃໝ່
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : sets.data?.length ? (
                  sets.data.map((set) => (
                    <TableRow key={set.id}>
                      <TableCell className="font-medium">{set.name}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant="secondary">{set.items.length}</Badge>
                      </TableCell>
                      <TableCell>
                        {new Set(
                          set.items.map((item) => JSON.stringify(item.price)),
                        ).size > 1
                          ? "ຫຼາຍກົດລາຄາ · ເບິ່ງລາຍ SKU"
                          : priceSummary(set.items[0]?.price ?? emptyPrice)}
                      </TableCell>
                      <TableCell className="hidden text-muted-foreground text-sm lg:table-cell">
                        {formatDateTimeLocal(set.updatedAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to="/app/product-sets/$id"
                          params={{ id: set.id }}
                          search={{ pageId }}
                          className={buttonVariants({
                            variant: "outline",
                            size: "sm",
                          })}
                        >
                          ຈັດການ
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="h-24 text-center text-muted-foreground"
                    >
                      ຍັງບໍ່ມີຊຸດສິນຄ້າ
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </Main>
    </>
  );
}

export function ProductSetDetailPage() {
  const { has } = usePermissions();
  const canManage = has("live:manage");
  const { id } = useParams({ from: "/app/product-sets/$id" });
  const search = useSearch({ from: "/app/product-sets/$id" });
  const nav = useNavigate({ from: "/app/product-sets/$id" });
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [price, setPrice] = useState<PriceInput>(emptyPrice);
  const [selected, setSelected] = useState<string[]>([]);
  const [prices, setPrices] = useState<ProductSetPrices>({});
  const [revision, setRevision] = useState<number | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [recovering, setRecovering] = useState(false);
  const [unresolvedSave, setUnresolvedSave] = useState(false);
  const readbackRetry = useRef<(() => Promise<void>) | null>(null);
  const leaveAllowed = useRef(false);
  const hydratedIdentity = useRef("");
  const [tab, setTab] = useState("selected");
  const [managingQueue, setManagingQueue] = useState(false);
  const [queueSearch, setQueueSearch] = useState("");
  const [queueReadiness, setQueueReadiness] = useState("all");
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSearchInput, setCatalogSearchInput] = useState("");
  const [catalogReadiness, setCatalogReadiness] = useState("all");
  const [catalogSelection, setCatalogSelection] = useState<AdminCatalogItem[]>(
    [],
  );
  const [removalSelection, setRemovalSelection] = useState<Set<string>>(
    () => new Set(),
  );
  const [dirty, setDirty] = useState(false);
  const [priceDialog, setPriceDialog] = useState<{
    previewItems: AdminCatalogItem[];
    commitItems: AdminCatalogItem[];
    initialPrice: PriceInput;
    scope: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const savingRef = useRef(false);
  const deletingRef = useRef(false);
  const debouncedCatalogSearch = useDebounceCallback(
    (next: string) => setCatalogSearch(next.trim()),
    300,
  );
  const pageId = search.pageId;
  const isNew = id === "new";
  const sets = useQuery({
    queryKey: listKey(pageId ?? ""),
    queryFn: () => productSetApi.list(pageId ?? ""),
    enabled: Boolean(pageId),
  });
  const saved = isNew ? undefined : sets.data?.find((set) => set.id === id);
  useEffect(() => {
    if (!saved || (dirty && hydratedIdentity.current === `${pageId}/${id}`))
      return;
    hydratedIdentity.current = `${pageId}/${id}`;
    setName(saved.name);
    setPrices(
      Object.fromEntries(
        saved.items.map((item) => [item.variantId, item.price]),
      ),
    );
    setRevision(saved.revision);
    leaveAllowed.current = false;
    setSelected(saved.items.map((item) => item.variantId));
    setCatalogSelection([]);
    setRemovalSelection(new Set());
    setDirty(false);
    setSaveError(null);
    setPriceDialog(null);
  }, [saved, dirty, id, pageId]);
  useEffect(() => {
    if (!isNew) return;
    setName("");
    setPrice(emptyPrice);
    setPrices({});
    setRevision(null);
    hydratedIdentity.current = `${pageId}/${id}`;
    leaveAllowed.current = false;
    setSelected([]);
    setCatalogSelection([]);
    setRemovalSelection(new Set());
    setDirty(false);
    setSaveError(null);
    setPriceDialog(null);
  }, [isNew, pageId, id]);
  useBlocker({
    shouldBlockFn: async () => {
      if (leaveAllowed.current) return false;
      if (
        savingRef.current ||
        deletingRef.current ||
        recovering ||
        unresolvedSave
      )
        return true;
      if (!dirty) return false;
      return !(await confirm({
        title: "ຍັງບໍ່ບັນທຶກ",
        description: "ກັບຄືນເພື່ອບັນທຶກ ຫຼືຖິ້ມການແກ້ໄຂແລ້ວອອກ",
        actionText: "ຖິ້ມການແກ້ໄຂ",
        cancelText: "ກັບຄືນ",
        ActionProps: { variant: "destructive" },
      }));
    },
    enableBeforeUnload: () =>
      !leaveAllowed.current &&
      (dirty ||
        savingRef.current ||
        deletingRef.current ||
        recovering ||
        unresolvedSave),
  });
  const catalog = useInfiniteQuery({
    queryKey: [
      "cf-product-set-catalog",
      pageId,
      catalogSearch,
      catalogReadiness,
    ],
    queryFn: ({ pageParam, signal }) =>
      productSetApi.catalog(
        {
          pageId: pageId ?? "",
          q: catalogSearch || undefined,
          limit: CATALOG_PAGE_LIMIT,
          offset: pageParam,
          groupByProduct: true,
          readiness:
            catalogReadiness === "all"
              ? undefined
              : (catalogReadiness as "ready" | "zero" | "preorder"),
        },
        signal,
      ),
    enabled: Boolean(pageId) && canManage,
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce(
        (sum, page) => sum + (page.products?.length ?? 0),
        0,
      );
      return loaded < lastPage.total ? loaded : undefined;
    },
  });
  const selectedCatalog = useQuery({
    queryKey: ["cf-product-set-catalog-exact", pageId, selected],
    queryFn: ({ signal }) =>
      productSetApi.catalog(
        {
          pageId: pageId ?? "",
          variantIds: selected,
          limit: 100,
          groupByProduct: true,
        },
        signal,
      ),
    enabled: Boolean(pageId) && selected.length > 0,
  });
  const catalogProducts = useMemo(
    () => catalog.data?.pages.flatMap((page) => page.products ?? []) ?? [],
    [catalog.data],
  );
  const selectedIds = useMemo(() => new Set(selected), [selected]);
  const selectedCatalogItems = selectedCatalog.data?.items ?? [];
  const orderedSelectedCatalogItems = useMemo(() => {
    const byId = new Map(
      selectedCatalogItems.map((item) => [item.variantId, item]),
    );
    return selected.flatMap((variantId) => {
      const item = byId.get(variantId);
      return item ? [item] : [];
    });
  }, [selected, selectedCatalogItems]);
  const selectedCatalogProductRows = selectedCatalog.data?.products ?? [];
  const selectedCatalogProducts = useMemo(
    () =>
      selectedCatalogProductRows
        .map((product) => ({
          ...product,
          variants: product.variants.filter((item) =>
            selectedIds.has(item.variantId),
          ),
        }))
        .filter((product) => product.variants.length > 0)
        .sort(
          (left, right) =>
            Math.min(
              ...left.variants.map((item) => selected.indexOf(item.variantId)),
            ) -
            Math.min(
              ...right.variants.map((item) => selected.indexOf(item.variantId)),
            ),
        ),
    [selectedCatalogProductRows, selected, selectedIds],
  );
  const filteredSelectedCatalogProducts = useMemo(() => {
    const query = queueSearch.trim().normalize("NFKC").toLowerCase();
    return selectedCatalogProducts.filter((product) => {
      const matchesSearch =
        !query ||
        product.name.normalize("NFKC").toLowerCase().includes(query) ||
        product.variants.some((item) =>
          item.sku.normalize("NFKC").toLowerCase().includes(query),
        );
      const matchesReadiness =
        queueReadiness === "all" ||
        (queueReadiness === "ready" &&
          product.variants.some(
            (item) => item.isPreorder || item.availableToAdd > 0,
          )) ||
        (queueReadiness === "zero" &&
          product.variants.every(
            (item) => !item.isPreorder && item.availableToAdd <= 0,
          )) ||
        (queueReadiness === "preorder" &&
          product.variants.some((item) => item.isPreorder));
      return matchesSearch && matchesReadiness;
    });
  }, [queueReadiness, queueSearch, selectedCatalogProducts]);
  const availableCatalogProducts = useMemo(
    () =>
      catalogProducts
        .map((product) => ({
          ...product,
          variants: product.variants.filter(
            (item) => !selectedIds.has(item.variantId),
          ),
        }))
        .filter((product) => product.variants.length > 0),
    [catalogProducts, selectedIds],
  );
  const unavailableSelectedCount = selectedCatalog.isSuccess
    ? Math.max(0, selected.length - selectedCatalogItems.length)
    : 0;
  const resolvedSelectedIds = useMemo(
    () => new Set(selectedCatalogItems.map((item) => item.variantId)),
    [selectedCatalogItems],
  );
  const unavailableSelectedIds = useMemo(
    () =>
      selectedCatalog.isSuccess
        ? selected.filter((variantId) => !resolvedSelectedIds.has(variantId))
        : [],
    [resolvedSelectedIds, selected, selectedCatalog.isSuccess],
  );
  const removeVariantIds = (variantIds: Iterable<string>) => {
    const removing = new Set(variantIds);
    if (removing.size === 0) return;
    setSelected((current) =>
      current.filter((variantId) => !removing.has(variantId)),
    );
    setRemovalSelection((current) => {
      const next = new Set(current);
      for (const variantId of removing) next.delete(variantId);
      return next;
    });
    setDirty(true);
  };
  const updateRemovalSelection = (variantIds: string[], checked: boolean) => {
    setRemovalSelection((current) =>
      updateCatalogSelection(current, variantIds, checked),
    );
  };
  const updatePendingCatalogSelection = (
    items: AdminCatalogItem[],
    checked: boolean,
  ) => {
    const currentIds = new Set(catalogSelection.map((item) => item.variantId));
    const next = updateCatalogSelection(
      currentIds,
      items.map((item) => item.variantId),
      checked,
    );
    if (next.size + selected.length > 100) {
      toast.error("ເລືອກໄດ້ສູງສຸດ 100 SKU ຕໍ່ຊຸດ");
      return;
    }
    setCatalogSelection(
      [
        ...new Map(
          [...catalogSelection, ...items].map((item) => [item.variantId, item]),
        ).values(),
      ].filter((item) => next.has(item.variantId)),
    );
  };
  const commitCatalogItems = (items: AdminCatalogItem[]) => {
    const itemIds = new Set(items.map((item) => item.variantId));
    const additions = items.filter((item) => !selectedIds.has(item.variantId));
    if (selected.length + additions.length > 100) {
      toast.error("ເລືອກໄດ້ສູງສຸດ 100 SKU ຕໍ່ຊຸດ");
      return;
    }
    setSelected((current) => [
      ...current,
      ...additions.map((item) => item.variantId),
    ]);
    if (additions.length > 0) setDirty(true);
    setCatalogSelection((current) =>
      current.filter((item) => !itemIds.has(item.variantId)),
    );
  };
  const openCatalogPriceDialog = (items: AdminCatalogItem[]) => {
    const additions = items.filter((item) => !selectedIds.has(item.variantId));
    if (selected.length + additions.length > 100) {
      toast.error("ເລືອກໄດ້ສູງສຸດ 100 SKU ຕໍ່ຊຸດ");
      return;
    }
    if (additions.length === 0) return;
    setPriceDialog({
      previewItems: additions,
      commitItems: additions,
      initialPrice: price,
      scope: "ເພີ່ມໃໝ່ເທົ່ານັ້ນ",
    });
  };
  const reviewCurrentPrices = () => {
    if (unavailableSelectedIds.length > 0) {
      toast.error("ມີ SKU ທີ່ບໍ່ພ້ອມໃຫ້ກວດລາຄາ. ນຳລາຍການນັ້ນອອກກ່ອນ");
      return;
    }
    if (orderedSelectedCatalogItems.length === 0) return;
    setPriceDialog({
      previewItems: orderedSelectedCatalogItems,
      commitItems: [],
      initialPrice: emptyPrice,
      scope: "ແກ້ລາຄາທັງຊຸດ",
    });
  };
  const moveProductGroup = (productId: string, nextPosition: number) => {
    const currentGroups = selectedCatalogProducts.map((product) => ({
      productId: product.productId,
      variantIds: product.variants.map((item) => item.variantId),
    }));
    const fromIndex = currentGroups.findIndex(
      (group) => group.productId === productId,
    );
    if (fromIndex < 0) return;
    const targetIndex = Math.max(
      0,
      Math.min(currentGroups.length - 1, nextPosition - 1),
    );
    if (fromIndex === targetIndex) return;
    const nextGroups = [...currentGroups];
    const [moving] = nextGroups.splice(fromIndex, 1);
    if (!moving) return;
    nextGroups.splice(targetIndex, 0, moving);
    const known = new Set(currentGroups.flatMap((group) => group.variantIds));
    setSelected((current) => [
      ...nextGroups.flatMap((group) => group.variantIds),
      ...current.filter((variantId) => !known.has(variantId)),
    ]);
    setDirty(true);
  };
  const save = async () => {
    if (
      savingRef.current ||
      deletingRef.current ||
      recovering ||
      unresolvedSave
    )
      return;
    setSaveError(null);
    if (!pageId || !name.trim() || !selected.length) {
      setSaveError(new Error("PRODUCT_SET_FIELDS_REQUIRED"));
      return;
    }
    if (
      (!isNew && revision === null) ||
      !selectedCatalog.isSuccess ||
      sets.isError
    )
      return;
    let items: ReturnType<typeof productSetItems>;
    try {
      items = productSetItems(selected, prices);
      for (const item of orderedSelectedCatalogItems)
        productSetEstimate(
          Number(item.price),
          prices[item.variantId] ?? emptyPrice,
        );
    } catch (error) {
      setSaveError(error);
      return;
    }
    const input = {
      pageId,
      name: name.trim(),
      items,
      expectedRevision: revision ?? undefined,
    };
    savingRef.current = true;
    setSaving(true);
    const acceptSaved = (
      result: Awaited<ReturnType<typeof productSetApi.create>>,
    ) => {
      client.setQueryData(listKey(pageId), (current: typeof sets.data) => [
        ...(current ?? []).filter((set) => set.id !== result.id),
        result,
      ]);
      setRevision(result.revision);
      setDirty(false);
      setSaveError(null);
      setUnresolvedSave(false);
      readbackRetry.current = null;
      leaveAllowed.current = true;
      toast.success("ບັນທຶກຊຸດສິນຄ້າແລ້ວ");
      void client.invalidateQueries({
        queryKey: ["workspace-product-sets", pageId],
      });
      void nav({
        to: "/app/product-sets/$id",
        params: { id: result.id },
        search: { pageId },
      });
    };
    try {
      const result = !isNew
        ? await productSetApi.update(id, input)
        : await productSetApi.create(input);
      // Read the saved owner record before presenting success.
      const records = await productSetApi.list(pageId);
      const readback = records.find((set) => set.id === result.id);
      if (
        !readback ||
        readback.revision !== result.revision ||
        readback.name !== input.name ||
        JSON.stringify(readback.items) !== JSON.stringify(input.items)
      )
        throw new Error("SAVE_READBACK_MISMATCH");
      acceptSaved(readback);
    } catch (error) {
      setSaveError(error);
      if (!(error instanceof WorkspaceRequestError)) {
        setRecovering(true);
        const inspectOutcome = async () => {
          setRecovering(true);
          try {
            const records = await productSetApi.list(pageId);
            const readback = records.find((set) =>
              isNew ? set.name === input.name : set.id === id,
            );
            if (
              readback &&
              readback.name === input.name &&
              JSON.stringify(readback.items) === JSON.stringify(input.items) &&
              (isNew ||
                readback.revision === (input.expectedRevision ?? -1) + 1)
            )
              acceptSaved(readback);
            else {
              setUnresolvedSave(false);
              readbackRetry.current = null;
              setSaveError(new Error("SAVE_OUTCOME_REVIEW_REQUIRED"));
            }
          } catch {
            setSaveError(new Error("SAVE_OUTCOME_UNKNOWN"));
            setUnresolvedSave(true);
          } finally {
            setRecovering(false);
          }
        };
        readbackRetry.current = inspectOutcome;
        await inspectOutcome();
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const removeSet = async () => {
    if (!saved || deletingRef.current || savingRef.current) return;
    const accepted = await confirm({
      title: `ລົບຊຸດ ${saved.name}?`,
      description: "ບໍ່ກະທົບ Feed ຫຼື Live ທີ່ເຄີຍເພີ່ມສິນຄ້າຈາກຊຸດນີ້",
      actionText: "ລົບຊຸດ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!accepted) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await productSetApi.remove(saved.id);
      await client.invalidateQueries({ queryKey: listKey(pageId ?? "") });
      toast.success("ລົບຊຸດສິນຄ້າແລ້ວ");
      leaveAllowed.current = true;
      nav({ to: "/app/product-sets", search: { pageId } });
    } catch {
      toast.error("ລົບຊຸດສິນຄ້າບໍ່ສຳເລັດ");
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };
  return (
    <>
      <Header />
      <Main>
        <div className="mx-auto max-w-5xl space-y-5">
          <Link
            to="/app/product-sets"
            search={{ pageId }}
            className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
          >
            <RiArrowLeftLine size={18} /> ກັບໄປລາຍການຊຸດສິນຄ້າ
          </Link>
          <div>
            <h1 className="font-semibold text-2xl">
              {isNew ? "ສ້າງຊຸດສິນຄ້າ" : "ລາຍລະອຽດຊຸດສິນຄ້າ"}
            </h1>
            <p className="text-muted-foreground text-sm">
              {isNew
                ? "ບັນທຶກໄວ້ກ່ອນ ແລ້ວເລືອກໃຊ້ໃນ Feed ຫຼື Live ໄດ້"
                : "ເບິ່ງ ແລະ ແກ້ໄຂສິນຄ້າໃນຊຸດນີ້"}
            </p>
          </div>
          <PagePicker
            value={pageId}
            onChange={(next) => nav({ search: { pageId: next } })}
          />
          {!pageId ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
              ເລືອກເພຈກ່ອນ
            </div>
          ) : !isNew && sets.isPending ? (
            <output className="block">ກຳລັງໂຫຼດຊຸດ…</output>
          ) : sets.isError ? (
            <div role="alert">
              <p>{workspaceErrorMessage(sets.error)}</p>
              <Button onClick={() => void sets.refetch()}>ລອງໃໝ່</Button>
            </div>
          ) : !isNew && !saved ? (
            <p role="alert">ບໍ່ພົບຊຸດສິນຄ້ານີ້ໃນເພຈທີ່ເລືອກ</p>
          ) : (
            <>
              <section
                className="space-y-2 rounded-lg border p-4"
                aria-label="ສະຫຼຸບຊຸດ"
              >
                <p>
                  {selectedCatalog.isSuccess
                    ? selectedCatalogProducts.length
                    : "—"}{" "}
                  ສິນຄ້າ · {selected.length} SKU · ພ້ອມຂາຍ{" "}
                  {selectedCatalog.isSuccess
                    ? selectedCatalogItems.filter(
                        (item) =>
                          item.isAvailable !== false &&
                          (item.isPreorder || item.availableToAdd > 0),
                      ).length
                    : "—"}
                </p>
                <p className="text-muted-foreground text-sm">
                  {saved
                    ? `ບັນທຶກລ່າສຸດ ${formatDateTimeLocal(saved.updatedAt)}`
                    : "ຊຸດໃໝ່ຍັງບໍ່ບັນທຶກ"}
                </p>
                <p className="text-muted-foreground text-xs">
                  ລາຄານີ້ຍັງບໍ່ລວມບໍລິການ ແລະຄ່າສົ່ງ · ໃຊ້ໂປຣຂອງງານຕ້ອງກວດໃນ Feed / Live
                </p>
              </section>
              {saveError ? (
                <div role="alert" className="space-y-2 rounded-lg border p-3">
                  <p>{workspaceErrorMessage(saveError)}</p>
                  {unresolvedSave ? (
                    <Button
                      variant="outline"
                      disabled={recovering}
                      onClick={() => void readbackRetry.current?.()}
                    >
                      ກວດຜົນບັນທຶກ
                    </Button>
                  ) : null}
                  {saveError instanceof WorkspaceRequestError &&
                  ["REVISION_CONFLICT", "PRODUCT_SET_REVIEW_REQUIRED"].includes(
                    saveError.code,
                  ) ? (
                    <Button
                      variant="outline"
                      onClick={async () => {
                        if (
                          await confirm({
                            title: "ໂຫຼດຊຸດລ່າສຸດ?",
                            description: "ຈະຖິ້ມການແກ້ໄຂທີ່ຍັງບໍ່ບັນທຶກ",
                            actionText: "ໂຫຼດໃໝ່",
                            cancelText: "ກັບຄືນ",
                          })
                        ) {
                          setDirty(false);
                          await sets.refetch();
                        }
                      }}
                    >
                      ໂຫຼດຊຸດລ່າສຸດ
                    </Button>
                  ) : null}
                </div>
              ) : null}
              <fieldset
                className="min-w-0 rounded-xl border bg-card"
                disabled={saving || deleting || recovering || unresolvedSave}
              >
                <div className="border-b p-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="w-full max-w-xl space-y-1">
                      <label
                        htmlFor="product-set-name"
                        className="text-muted-foreground text-xs"
                      >
                        ຊື່ຊຸດສິນຄ້າ
                      </label>
                      <Input
                        id="product-set-name"
                        disabled={
                          !canManage || saving || deleting || recovering
                        }
                        value={name}
                        onChange={(event) => {
                          setName(event.target.value);
                          setDirty(true);
                        }}
                        placeholder="ຊື່ຊຸດສິນຄ້າ"
                        maxLength={120}
                      />
                    </div>
                    {saved && canManage ? (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => void removeSet()}
                        disabled={deleting || saving}
                      >
                        <RiDeleteBinLine size={18} />
                        {deleting ? "ກຳລັງລົບ..." : "ລົບຊຸດ"}
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
                  <output className="text-muted-foreground text-sm">
                    {dirty
                      ? "ມີການປ່ຽນແປງທີ່ຍັງບໍ່ບັນທຶກ"
                      : isNew
                        ? "ຍັງບໍ່ບັນທຶກ"
                        : "ບັນທຶກແລ້ວ"}
                  </output>
                  {canManage ? (
                    <Button
                      onClick={() => void save()}
                      disabled={
                        !selected.length ||
                        saving ||
                        deleting ||
                        recovering ||
                        !selectedCatalog.isSuccess ||
                        sets.isError
                      }
                    >
                      <RiSaveLine size={18} />
                      {saving
                        ? "ກຳລັງບັນທຶກ..."
                        : isNew
                          ? "ບັນທຶກຊຸດ"
                          : "ບັນທຶກການແກ້ໄຂ"}
                    </Button>
                  ) : (
                    <Badge variant="secondary">ອ່ານຢ່າງດຽວ</Badge>
                  )}
                </div>
                <Tabs value={tab} onValueChange={setTab} className="min-w-0">
                  <TabsList className="flex h-auto w-full flex-wrap justify-start rounded-none border-b bg-transparent p-0">
                    <TabsTrigger className="min-h-11" value="selected">
                      ສິນຄ້າທີ່ເພີ່ມແລ້ວ ({selected.length})
                    </TabsTrigger>
                    {canManage ? (
                      <TabsTrigger className="min-h-11" value="catalog">
                        ສິນຄ້າທີ່ຍັງບໍ່ໄດ້ເພີ່ມ
                      </TabsTrigger>
                    ) : null}
                  </TabsList>
                  <TabsContent value="selected" className="m-0">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
                      <div>
                        <h2 className="font-medium">ສິນຄ້າໃນຊຸດ</h2>
                        <p className="text-muted-foreground text-xs">
                          {selectedCatalog.isSuccess
                            ? selectedCatalogProducts.length
                            : "—"}{" "}
                          ສິນຄ້າ · {selected.length} SKU
                        </p>
                        <p
                          className={
                            dirty
                              ? "text-amber-700 text-xs dark:text-amber-400"
                              : "text-muted-foreground text-xs"
                          }
                          aria-live="polite"
                        >
                          {dirty
                            ? "ມີການປ່ຽນແປງທີ່ຍັງບໍ່ບັນທຶກ"
                            : isNew
                              ? "ຍັງບໍ່ບັນທຶກ"
                              : "ບັນທຶກແລ້ວ"}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {canManage ? (
                          <>
                            <Button
                              type="button"
                              id="product-set-edit-all"
                              variant="outline"
                              onClick={reviewCurrentPrices}
                              disabled={
                                !selected.length ||
                                !selectedCatalog.isSuccess ||
                                saving ||
                                deleting
                              }
                            >
                              <RiCheckLine size={18} />
                              ແກ້ລາຄາທັງຊຸດ ({selected.length})
                            </Button>
                            <Button
                              variant={managingQueue ? "secondary" : "outline"}
                              aria-pressed={managingQueue}
                              onClick={() =>
                                setManagingQueue((value) => !value)
                              }
                            >
                              {managingQueue ? (
                                <RiCheckLine size={18} />
                              ) : (
                                <RiDragMove2Line size={18} />
                              )}
                              {managingQueue ? "ຈັດລຳດັບແລ້ວ" : "ຈັດລຳດັບ"}
                            </Button>
                          </>
                        ) : (
                          <Badge variant="secondary">ອ່ານຢ່າງດຽວ</Badge>
                        )}
                      </div>
                    </div>
                    {canManage && managingQueue ? (
                      <p className="border-b px-4 py-3 text-muted-foreground text-xs">
                        ລາກ ຫຼື ໃສ່ລຳດັບເພື່ອຈັດຄິວ. ລຳດັບນີ້ຈະຖືກນຳໄປໃຊ້ຕອນເພີ່ມຊຸດເຂົ້າ Feed ຫຼື
                        Live.
                      </p>
                    ) : null}
                    <Collapsible className="border-b p-4">
                      <div className="flex gap-2">
                        <Input
                          aria-label="ຄົ້ນຫາສິນຄ້າໃນຊຸດ"
                          placeholder="ຄົ້ນຫາຊື່ ຫຼື SKU…"
                          value={queueSearch}
                          onChange={(event) =>
                            setQueueSearch(event.target.value)
                          }
                          className="min-h-11 min-w-0 flex-1"
                        />
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="outline"
                            className="min-h-11 shrink-0"
                          >
                            ຕົວກອງ
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent className="pt-3">
                        <WorkspaceSelect
                          label="ຄວາມພ້ອມ"
                          value={queueReadiness}
                          onChange={setQueueReadiness}
                          items={[
                            ["all", "ທັງໝົດ"],
                            ["ready", "ພ້ອມຂາຍ"],
                            ["zero", "ສະຕັອກ 0"],
                            ["preorder", "Preorder"],
                          ]}
                        />
                      </CollapsibleContent>
                    </Collapsible>
                    {!selectedCatalog.isLoading &&
                    unavailableSelectedCount > 0 ? (
                      <p className="border-b bg-muted/30 p-3 text-muted-foreground text-xs">
                        {unavailableSelectedCount} SKU ບໍ່ຢູ່ໃນລາຍການຄັງປັດຈຸບັນ
                        ແຕ່ຍັງຖືກເກັບໄວ້ໃນຊຸດ
                      </p>
                    ) : null}
                    {selected.length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
                        <p className="text-sm">ຍັງບໍ່ມີສິນຄ້າໃນຊຸດ</p>
                        {canManage ? (
                          <Button
                            variant="outline"
                            onClick={() => setTab("catalog")}
                          >
                            ເພີ່ມສິນຄ້າ
                          </Button>
                        ) : null}
                      </div>
                    ) : selectedCatalog.isError ? (
                      <div role="alert" className="space-y-2 p-4">
                        <p>{workspaceErrorMessage(selectedCatalog.error)}</p>
                        <Button
                          variant="outline"
                          onClick={() => void selectedCatalog.refetch()}
                        >
                          ລອງໃໝ່
                        </Button>
                      </div>
                    ) : selectedCatalog.isLoading ? (
                      <div className="p-8 text-center text-muted-foreground text-sm">
                        ກຳລັງໂຫຼດສິນຄ້າໃນຊຸດ...
                      </div>
                    ) : filteredSelectedCatalogProducts.length === 0 &&
                      unavailableSelectedIds.length === 0 ? (
                      <div className="p-8 text-center text-muted-foreground text-sm">
                        ບໍ່ພົບສິນຄ້າຕາມຄຳຄົ້ນ ຫຼື ຕົວກອງ
                      </div>
                    ) : (
                      <div
                        className={`grid max-h-[42rem] gap-2 overflow-auto overscroll-contain p-3 ${
                          managingQueue || removalSelection.size > 0
                            ? "grid-cols-1"
                            : "grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3"
                        }`}
                      >
                        {filteredSelectedCatalogProducts.map((product) => {
                          const position =
                            selectedCatalogProducts.findIndex(
                              (candidate) =>
                                candidate.productId === product.productId,
                            ) + 1;
                          const groupVariantIds = product.variants.map(
                            (item) => item.variantId,
                          );
                          const actions: WorkspaceProductCardAction[] =
                            canManage
                              ? [
                                  {
                                    id: "remove-product",
                                    label: "ນຳສິນຄ້າອອກຈາກຊຸດ",
                                    icon: (
                                      <RiDeleteBinLine
                                        className="size-4"
                                        aria-hidden
                                      />
                                    ),
                                    destructive: true,
                                    onSelect: () =>
                                      removeVariantIds(groupVariantIds),
                                  },
                                ]
                              : [];
                          return (
                            <WorkspaceProductCard
                              key={product.productId}
                              productId={product.productId}
                              name={product.name}
                              imageUrl={product.variants[0]?.imageUrl}
                              subtitle={
                                <>
                                  ລຳດັບ {position} ·{" "}
                                  {product.variants
                                    .slice(0, 3)
                                    .map(
                                      (item) =>
                                        item.defaultCfCode ??
                                        item.suggestedCfCode ??
                                        item.sku,
                                    )
                                    .join(" · ")}
                                  {product.variants.length > 3
                                    ? ` · +${product.variants.length - 3}`
                                    : ""}
                                </>
                              }
                              price={
                                <span className="whitespace-normal text-xs">
                                  {product.variants.length} SKU ·{" "}
                                  {new Set(
                                    product.variants.map((item) =>
                                      JSON.stringify(prices[item.variantId]),
                                    ),
                                  ).size > 1
                                    ? "ຫຼາຍກົດລາຄາ"
                                    : priceSummary(
                                        prices[
                                          product.variants[0]?.variantId ?? ""
                                        ] ?? emptyPrice,
                                      )}
                                </span>
                              }
                              variants={product.variants.map((item) => ({
                                id: item.variantId,
                                selectionLabel: `ເລືອກ ${item.sku}`,
                                content: (
                                  <>
                                    <strong className="text-primary">
                                      {item.defaultCfCode ??
                                        item.suggestedCfCode ??
                                        item.sku}
                                    </strong>
                                    <span className="block truncate text-muted-foreground text-xs">
                                      {item.sku} ·{" "}
                                      {item.isAvailable === false
                                        ? "ບໍ່ພ້ອມໃຊ້ງານ · ສາມາດນຳອອກໄດ້"
                                        : item.isPreorder
                                          ? "Preorder"
                                          : item.availableToAdd <= 0
                                            ? "ສະຕັອກ 0"
                                            : `ຄົງເຫຼືອ ${item.availableToAdd}`}
                                    </span>
                                  </>
                                ),
                                mobilePrice: (
                                  <ProductSetPrice
                                    item={item}
                                    price={prices[item.variantId] ?? emptyPrice}
                                  />
                                ),
                                desktopPrice: (
                                  <ProductSetPrice
                                    item={item}
                                    price={prices[item.variantId] ?? emptyPrice}
                                  />
                                ),
                                actions: canManage
                                  ? [
                                      {
                                        id: "price",
                                        disabled:
                                          saving || deleting || recovering,
                                        label: "ແກ້ລາຄາ SKU ນີ້",
                                        onSelect: () =>
                                          setPriceDialog({
                                            previewItems: [item],
                                            commitItems: [],
                                            initialPrice:
                                              prices[item.variantId] ??
                                              emptyPrice,
                                            scope: "ແກ້ລາຄາລາຍການດຽວ",
                                          }),
                                      },
                                      {
                                        id: "remove",
                                        disabled:
                                          saving || deleting || recovering,
                                        label: "ນຳອອກ",
                                        icon: (
                                          <RiDeleteBinLine
                                            className="size-4"
                                            aria-hidden
                                          />
                                        ),
                                        destructive: true,
                                        onSelect: () =>
                                          removeVariantIds([item.variantId]),
                                      },
                                    ]
                                  : [],
                              }))}
                              selectedVariantIds={
                                canManage ? removalSelection : undefined
                              }
                              onSelectionChange={
                                canManage ? updateRemovalSelection : undefined
                              }
                              selectionDisabled={
                                !canManage || saving || deleting
                              }
                              actions={actions}
                              draggable={
                                canManage &&
                                managingQueue &&
                                !saving &&
                                !deleting
                              }
                              onDragStart={(event) =>
                                event.dataTransfer.setData(
                                  "text/plain",
                                  product.productId,
                                )
                              }
                              onDrop={(event) => {
                                event.preventDefault();
                                const sourceId =
                                  event.dataTransfer.getData("text/plain");
                                if (
                                  managingQueue &&
                                  sourceId &&
                                  sourceId !== product.productId
                                )
                                  moveProductGroup(sourceId, position);
                              }}
                              management={
                                canManage && managingQueue ? (
                                  <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2">
                                    <RiDragMove2Line
                                      className="size-5 text-muted-foreground"
                                      aria-label="ຈັດລຳດັບ"
                                    />
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="size-11"
                                      disabled={position === 1}
                                      aria-label={`ເລື່ອນ ${product.name} ຂຶ້ນ`}
                                      onClick={() =>
                                        moveProductGroup(
                                          product.productId,
                                          position - 1,
                                        )
                                      }
                                    >
                                      <RiArrowUpLine size={18} />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className="size-11"
                                      disabled={
                                        position ===
                                        selectedCatalogProducts.length
                                      }
                                      aria-label={`ເລື່ອນ ${product.name} ລົງ`}
                                      onClick={() =>
                                        moveProductGroup(
                                          product.productId,
                                          position + 1,
                                        )
                                      }
                                    >
                                      <RiArrowDownLine size={18} />
                                    </Button>
                                    <label
                                      htmlFor={`product-set-position-${product.productId}`}
                                      className="ml-auto flex min-h-11 items-center gap-2 text-sm"
                                    >
                                      <span>ລຳດັບ</span>
                                      <Input
                                        id={`product-set-position-${product.productId}`}
                                        key={position}
                                        type="number"
                                        min={1}
                                        max={selectedCatalogProducts.length}
                                        defaultValue={position}
                                        className="min-h-11 w-20"
                                        onBlur={(event) =>
                                          moveProductGroup(
                                            product.productId,
                                            Number(event.target.value),
                                          )
                                        }
                                      />
                                    </label>
                                  </div>
                                ) : null
                              }
                            />
                          );
                        })}
                        {unavailableSelectedIds.map((variantId) => (
                          <div
                            key={variantId}
                            className="flex min-h-14 items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="font-medium text-sm">
                                ສິນຄ້າບໍ່ພ້ອມໃຊ້ງານ
                              </p>
                              <p className="text-muted-foreground text-xs">
                                ລຳດັບ {selected.indexOf(variantId) + 1} ·
                                ສາມາດນຳອອກຈາກຊຸດໄດ້
                              </p>
                            </div>
                            {canManage ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => removeVariantIds([variantId])}
                              >
                                <RiDeleteBinLine size={18} /> ນຳອອກ
                              </Button>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    )}
                    {canManage && removalSelection.size > 0 ? (
                      <section
                        className="sticky bottom-0 z-20 flex flex-wrap items-center gap-2 border-t bg-background p-3 shadow-lg"
                        aria-label="ລາຍການທີ່ເລືອກ"
                      >
                        <p className="mr-auto text-sm">
                          ເລືອກ {removalSelection.size} SKU
                        </p>
                        <Button
                          variant="outline"
                          disabled={
                            saving || deleting || !selectedCatalog.isSuccess
                          }
                          onClick={() => {
                            const items = orderedSelectedCatalogItems.filter(
                              (item) => removalSelection.has(item.variantId),
                            );
                            if (items.length !== removalSelection.size)
                              return toast.error("ມີ SKU ທີ່ບໍ່ພ້ອມ");
                            setPriceDialog({
                              previewItems: items,
                              commitItems: [],
                              initialPrice: emptyPrice,
                              scope: "ແກ້ລາຄາທີ່ເລືອກ",
                            });
                          }}
                        >
                          ແກ້ລາຄາທີ່ເລືອກ ({removalSelection.size})
                        </Button>
                        <Button
                          type="button"
                          variant="destructive"
                          disabled={saving || deleting}
                          onClick={() => removeVariantIds(removalSelection)}
                        >
                          <RiDeleteBinLine size={18} />
                          ນຳອອກທີ່ເລືອກ ({removalSelection.size})
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setRemovalSelection(new Set())}
                        >
                          ລ້າງທີ່ເລືອກ
                        </Button>
                      </section>
                    ) : null}
                  </TabsContent>
                  {canManage ? (
                    <TabsContent value="catalog" className="m-0">
                      <div className="space-y-4 border-b p-4">
                        <div>
                          <h2 className="font-semibold text-lg">
                            ເລືອກສິນຄ້າເຂົ້າຊຸດ
                          </h2>
                          <p className="mt-1 text-muted-foreground text-sm">
                            ເພີ່ມທັງສິນຄ້າໄດ້ໃນຄັ້ງດຽວ ຫຼືເປີດຕົວເລືອກເພື່ອເລືອກແຕ່ບາງ SKU.
                          </p>
                        </div>
                        <Input
                          id="product-set-catalog-search"
                          placeholder="ຄົ້ນຫາສິນຄ້າ / SKU / ສະແກນ barcode"
                          value={catalogSearchInput}
                          onChange={(event) => {
                            setCatalogSearchInput(event.target.value);
                            debouncedCatalogSearch(event.target.value);
                          }}
                          className="min-h-11"
                        />
                        <WorkspaceSelect
                          label="ຄວາມພ້ອມ"
                          value={catalogReadiness}
                          onChange={(value) => {
                            setCatalogReadiness(value);
                            setCatalogSelection([]);
                          }}
                          items={[
                            ["all", "ທັງໝົດ ລວມສະຕັອກ 0"],
                            ["ready", "ພ້ອມຂາຍ"],
                            ["zero", "ສະຕັອກ 0"],
                            ["preorder", "Preorder"],
                          ]}
                        />
                        <section className="space-y-2 rounded-lg border bg-muted/20 p-3">
                          <div>
                            <h3 className="font-medium text-sm">
                              ຕັ້ງລາຄາ / ສ່ວນຫຼຸດ ກ່ອນເພີ່ມ
                            </h3>
                            <p className="text-muted-foreground text-xs">
                              ໃຊ້ສຳລັບ SKU ທີ່ເພີ່ມໃໝ່ເທົ່ານັ້ນ · ບໍ່ປ່ຽນລາຄາລາຍການເກົ່າ
                            </p>
                          </div>
                          <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_minmax(0,12rem)_auto] sm:items-end">
                            <WorkspaceSelect
                              label="ລາຄາທີ່ໃຊ້"
                              value={price.mode}
                              onChange={(mode) => {
                                setPrice(
                                  mode === "inherit" || mode === "none"
                                    ? { mode }
                                    : {
                                        mode: mode as
                                          | "sale"
                                          | "percent"
                                          | "fixed",
                                        value: price.value ?? 0,
                                      },
                                );
                              }}
                              items={[
                                ["inherit", "ໃຊ້ໂປຣຂອງງານ"],
                                ["sale", "ກອກລາຄາຂາຍຈິງ"],
                                ["percent", "ຫຼຸດ %"],
                                ["fixed", "ຫຼຸດເປັນກີບ"],
                                ["none", "ບໍ່ຫຼຸດລາຄາ"],
                              ]}
                            />
                            {price.mode !== "inherit" &&
                            price.mode !== "none" ? (
                              <Input
                                aria-label={
                                  price.mode === "sale"
                                    ? "ລາຄາຂາຍຈິງ (ກີບ)"
                                    : price.mode === "percent"
                                      ? "ສ່ວນຫຼຸດ (%)"
                                      : "ສ່ວນຫຼຸດ (ກີບ)"
                                }
                                step={
                                  price.mode === "percent"
                                    ? 0.01
                                    : price.mode === "fixed"
                                      ? 1000
                                      : 1
                                }
                                type="number"
                                min={0}
                                max={price.mode === "percent" ? 100 : 999999999}
                                value={price.value ?? ""}
                                onChange={(event) => {
                                  setPrice({
                                    ...price,
                                    value:
                                      event.target.value === ""
                                        ? undefined
                                        : Number(event.target.value),
                                  });
                                }}
                                className="min-h-11"
                              />
                            ) : null}
                            <Button
                              type="button"
                              variant="secondary"
                              className="min-h-11"
                              disabled={
                                catalogSelection.length === 0 ||
                                saving ||
                                deleting
                              }
                              onClick={() =>
                                openCatalogPriceDialog(catalogSelection)
                              }
                            >
                              <RiCheckLine size={18} />
                              ກວດລາຄາທີ່ເລືອກ ({catalogSelection.length})
                            </Button>
                          </div>
                        </section>
                        <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                          <p className="text-muted-foreground text-xs">
                            ເພີ່ມ SKU ແລ້ວ ກົດບັນທຶກເພື່ອໃຊ້ຊຸດນີ້ຕໍ່ໄປ
                          </p>
                        </div>
                      </div>
                      <QueryState
                        result={catalog}
                        isFetching={
                          catalog.isFetching && !catalog.isFetchingNextPage
                        }
                      >
                        {availableCatalogProducts.length > 0 ? (
                          <div className="space-y-3 p-3">
                            <WorkspaceCatalogCards
                              products={availableCatalogProducts}
                              selectedItems={catalogSelection}
                              disabled={saving || deleting}
                              hasNextPage={Boolean(catalog.hasNextPage)}
                              isFetchingNextPage={catalog.isFetchingNextPage}
                              onLoadMore={() => void catalog.fetchNextPage()}
                              onSelectionChange={updatePendingCatalogSelection}
                              onCommit={openCatalogPriceDialog}
                              onClear={() => setCatalogSelection([])}
                              allowWholeProductCommitWithSelection
                              reviewBeforeCommit
                              filtered={Boolean(
                                catalogSearch || catalogReadiness !== "all",
                              )}
                            />
                          </div>
                        ) : (
                          <p className="p-8 text-center text-muted-foreground text-sm">
                            ບໍ່ພົບສິນຄ້າທີ່ຍັງບໍ່ໄດ້ເພີ່ມ
                          </p>
                        )}
                      </QueryState>
                    </TabsContent>
                  ) : null}
                </Tabs>
              </fieldset>
              {saved && canManage ? (
                <ProductSetDestination
                  template={saved}
                  disabled={
                    dirty || saving || deleting || recovering || unresolvedSave
                  }
                />
              ) : null}
              {canManage && priceDialog ? (
                <WorkspaceCatalogPriceDialog
                  items={priceDialog.previewItems}
                  initialPrice={priceDialog.initialPrice}
                  scope={priceDialog.scope}
                  busy={saving || deleting}
                  onClose={() => setPriceDialog(null)}
                  onReturnFocus={() => {
                    const focusId = priceDialog.commitItems.length
                      ? "product-set-catalog-search"
                      : priceDialog.previewItems.length === 1
                        ? `workspace-variant-${priceDialog.previewItems[0]?.variantId}`
                        : "product-set-edit-all";
                    (
                      document.getElementById(focusId) ??
                      document.getElementById("product-set-edit-all")
                    )?.focus();
                  }}
                  onConfirm={(nextPrice) => {
                    setPrices((current) =>
                      editProductSetPrices(
                        current,
                        priceDialog.previewItems.map((item) => item.variantId),
                        nextPrice,
                      ),
                    );
                    setDirty(true);
                    commitCatalogItems(priceDialog.commitItems);
                    setPriceDialog(null);
                  }}
                />
              ) : null}
            </>
          )}
        </div>
      </Main>
    </>
  );
}
